import { AlertTriangle, CheckCircle2 } from "lucide-react";

const warningSigns = [
  {
    sign: "You're the only one who can approve expenses",
    fix: "Set approval thresholds. Managers approve under $X, you only see large purchases."
  },
  {
    sign: "Product decisions wait for your calendar",
    fix: "Empower a product lead with decision authority. Review weekly, not per-decision."
  },
  {
    sign: "You're still writing production code",
    fix: "Your job is to build the team, not the product. Hire engineers you trust."
  },
  {
    sign: "You answer customer support tickets",
    fix: "Build a support team. Your time is worth $500+/hour—use it strategically."
  },
  {
    sign: "Team members CC you on everything",
    fix: "This is learned helplessness. Actively redirect: 'You own this. Update me weekly.'"
  },
  {
    sign: "You have more than 8 direct reports",
    fix: "Add a management layer. 5-7 directs is optimal for effective leadership."
  },
  {
    sign: "You work weekends while the team doesn't",
    fix: "You're bottlenecking. If you're the only one working overtime, delegation has failed."
  },
  {
    sign: "You can't take a week off without fires",
    fix: "The 'vacation test'—if things break when you're gone, you haven't delegated."
  }
];

export const StartupFailureWarningSign = () => {
  return (
    <section className="py-10 sm:py-12">
      <h2 id="warning-signs" className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2">
        <AlertTriangle className="h-6 w-6 text-amber-500" />
        Warning Signs You're Not Delegating
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        Recognize these patterns? They indicate you're becoming a bottleneck—and it will limit your company's growth.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        {warningSigns.map((item, index) => (
          <div 
            key={index}
            className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors group"
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <p className="font-medium text-foreground">{item.sign}</p>
            </div>
            <div className="flex items-start gap-3 ml-8">
              <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">{item.fix}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Final CTA */}
      <div className="mt-10 bg-primary/5 border border-primary/20 rounded-xl p-6 sm:p-8 text-center">
        <h3 className="text-xl font-semibold text-foreground mb-3">
          The Ultimate Delegation Test
        </h3>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-4">
          Ask yourself: <strong className="text-foreground">"If I disappeared for 30 days, what would break?"</strong>
        </p>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Whatever your answer is—that's what you need to delegate next. The goal isn't to make yourself unnecessary; it's to make yourself available for the work only you can do.
        </p>
      </div>
    </section>
  );
};
